import React from "react";
import {
  Navigate,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { createGsbApp, type GsbHostAdapters } from "@gsb-core/app-kit";
import { AVAILABLE_LANGUAGES } from "@gsb-core/react-multilang";
import { brand } from "@gsb-core/brand";
import { resolveWorkspaceHost } from "./workspace-host";
import NotFound from "./not-found";
import { withLanguagePrefix } from "./language-path";

export type WorkspaceRouteShell = "public" | "dashboard";
export interface WorkspaceRouteManifestEntry {
  readonly path: string;
  readonly shell: WorkspaceRouteShell;
  readonly component: React.ComponentType;
  readonly identity: string;
}

const loadDashboardRoutes = () => import("./dashboard-routes");
const loadSettingsRoutes = () => import("./settings-dashboard-routes");

function readEnv(name: string): string | undefined {
  const value: unknown = import.meta.env?.[name];
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}
const LoginRoute = React.lazy(() =>
  import("./auth-feature").then(({ authFeatureModule }) => ({
    default: authFeatureModule.routes["/login"]!.component,
  })),
);
const ResetPasswordRoute = React.lazy(() =>
  import("./auth-feature").then(({ authFeatureModule }) => ({
    default: authFeatureModule.routes["/login/reset-password"]!.component,
  })),
);
const TermsRoute = React.lazy(() =>
  import("./legal-feature").then(({ legalFeatureModule }) => ({
    default: legalFeatureModule.routes["/terms"]!.component,
  })),
);
const PrivacyRoute = React.lazy(() =>
  import("./legal-feature").then(({ legalFeatureModule }) => ({
    default: legalFeatureModule.routes["/privacy"]!.component,
  })),
);
const CookiesRoute = React.lazy(() =>
  import("./legal-feature").then(({ legalFeatureModule }) => ({
    default: legalFeatureModule.routes["/cookies"]!.component,
  })),
);
const RefundsRoute = React.lazy(() =>
  import("./legal-feature").then(({ legalFeatureModule }) => ({
    default: legalFeatureModule.routes["/refunds"]!.component,
  })),
);
const FaqRoute = React.lazy(() =>
  import("./legal-feature").then(({ legalFeatureModule }) => ({
    default: legalFeatureModule.routes["/faq"]!.component,
  })),
);
const StatusRoute = React.lazy(() =>
  import("./status-route").then(({ StatusRoute }) => ({
    default: StatusRoute,
  })),
);
const DashboardOverviewRoute = React.lazy(() =>
  import("./dashboard-overview-route").then(({ DashboardOverviewRoute }) => ({
    default: DashboardOverviewRoute,
  })),
);
const AuthenticationDashboardRoute = React.lazy(() =>
  import("./authentication-dashboard-route").then(
    ({ AuthenticationDashboardRoute }) => ({
      default: AuthenticationDashboardRoute,
    }),
  ),
);
const FunctionWorkspace = React.lazy(() =>
  import("./components/function/FunctionWorkspace").then(
    ({ FunctionWorkspace }) => ({ default: FunctionWorkspace }),
  ),
);
const SystemRoute = React.lazy(() =>
  import("./system-routes").then(({ SystemRoute }) => ({
    default: SystemRoute,
  })),
);
const StorageDashboardRoute = React.lazy(() =>
  import("./storage-dashboard-route").then(({ StorageDashboardRoute }) => ({
    default: StorageDashboardRoute,
  })),
);
const AssistantDashboardRoute = React.lazy(() =>
  import("./assistant-dashboard-route").then(({ AssistantDashboardRoute }) => ({
    default: AssistantDashboardRoute,
  })),
);
const ApiConsoleRoute = React.lazy(() =>
  import("./api-console-route").then(({ ApiConsoleRoute }) => ({
    default: ApiConsoleRoute,
  })),
);
const EntityDefWizard = React.lazy(
  () => import("./components/database/entity-def-wizard"),
);
const DatabaseEntityRoute = React.lazy(() =>
  loadDashboardRoutes().then(({ DatabaseEntityRoute }) => ({
    default: DatabaseEntityRoute,
  })),
);
const DatabaseOverviewRoute = React.lazy(() =>
  loadDashboardRoutes().then(({ DatabaseOverviewRoute }) => ({
    default: DatabaseOverviewRoute,
  })),
);
const QueryBuilderRoute = React.lazy(() =>
  loadDashboardRoutes().then(({ QueryBuilderRoute }) => ({
    default: QueryBuilderRoute,
  })),
);
const SettingsOverviewRoute = React.lazy(() =>
  loadDashboardRoutes().then(({ SettingsOverviewRoute }) => ({
    default: SettingsOverviewRoute,
  })),
);
const GeneralSettingsRoute = React.lazy(() =>
  loadSettingsRoutes().then(({ GeneralSettingsRoute }) => ({
    default: GeneralSettingsRoute,
  })),
);
const SecuritySettingsRoute = React.lazy(() =>
  loadSettingsRoutes().then(({ SecuritySettingsRoute }) => ({
    default: SecuritySettingsRoute,
  })),
);
const ModuleSettingsRoute = React.lazy(() =>
  import("./module-settings-route").then(({ ModuleSettingsRoute }) => ({
    default: ModuleSettingsRoute,
  })),
);
const SmtpSettingsRoute = React.lazy(() =>
  import("./smtp-settings-route").then(({ SmtpSettingsRoute }) => ({
    default: SmtpSettingsRoute,
  })),
);
const DomainsSettingsRoute = React.lazy(() =>
  loadSettingsRoutes().then(({ DomainsSettingsRoute }) => ({
    default: DomainsSettingsRoute,
  })),
);
const TeamMembersRoute = React.lazy(() =>
  import("./team-members-route").then(({ TeamMembersRoute }) => ({
    default: TeamMembersRoute,
  })),
);
const WorkflowDashboardRoute = React.lazy(() =>
  import("./workflow-dashboard-route").then(({ WorkflowDashboardRoute }) => ({
    default: WorkflowDashboardRoute,
  })),
);
const WorkflowDesignerRoute = React.lazy(() =>
  import("./workflow-dashboard-route").then(({ WorkflowDesignerRoute }) => ({
    default: WorkflowDesignerRoute,
  })),
);
const WorkflowInstancesRoute = React.lazy(() =>
  import("./workflow-dashboard-route").then(({ WorkflowInstancesRoute }) => ({
    default: WorkflowInstancesRoute,
  })),
);
const WorkflowInstanceRoute = React.lazy(() =>
  import("./workflow-dashboard-route").then(({ WorkflowInstanceRoute }) => ({
    default: WorkflowInstanceRoute,
  })),
);
const EntityCreatePage = () => {
  const [searchParams] = useSearchParams();
  return React.createElement(EntityDefWizard, {
    mode: "create",
    cloneEntityDefId: searchParams.get("clone") ?? undefined,
  });
};
const EntityUpdatePage = () => {
  const { id } = useParams<{ id: string }>();
  return React.createElement(EntityDefWizard, {
    mode: "update",
    entityDefId: id,
  });
};
// Legacy `/data` suffix kept as a redirect so existing links do not break.
const DatabaseEntityDataRedirect = () => {
  const { name } = useParams<{ name: string }>();
  const { pathname } = useLocation();
  return React.createElement(Navigate, {
    to: withLanguagePrefix(`/dashboard/database/${name ?? ""}`, pathname),
    replace: true,
  });
};

export const publicRoutes = {};
export const protectedRoutes = {};
export function isWorkspaceRootPath(pathname: string): boolean {
  if (pathname === "/") return true;
  const language = pathname.slice(1).replace(/\/$/, "");
  return (
    !language.includes("/") &&
    (Object.prototype.hasOwnProperty.call(AVAILABLE_LANGUAGES, language) ||
      /^[a-z]{2}(?:-[A-Z]{2})?$/.test(language))
  );
}

export function workspaceRootRedirectPath(pathname: string): string {
  return withLanguagePrefix("/dashboard", pathname);
}

const WorkspaceRoot = () => {
  const location = useLocation();
  return isWorkspaceRootPath(location.pathname)
    ? React.createElement(Navigate, {
        to: `${workspaceRootRedirectPath(location.pathname)}${location.search}${location.hash}`,
        replace: true,
      })
    : React.createElement(NotFound);
};
export const workspaceRouteManifest: readonly WorkspaceRouteManifestEntry[] = [
  {
    path: "/",
    shell: "public",
    component: WorkspaceRoot,
    identity: brand.workspaceName,
  },
  {
    path: "/login",
    shell: "public",
    component: LoginRoute,
    identity: "Welcome back",
  },
  {
    path: "/forgot-password",
    shell: "public",
    component: ResetPasswordRoute,
    identity: "Reset your password",
  },
  {
    path: "/login/reset-password",
    shell: "public",
    component: ResetPasswordRoute,
    identity: "Reset your password",
  },
  {
    path: "/privacy",
    shell: "public",
    component: PrivacyRoute,
    identity: "Privacy Policy",
  },
  {
    path: "/terms",
    shell: "public",
    component: TermsRoute,
    identity: "Terms of Service",
  },
  {
    path: "/refunds",
    shell: "public",
    component: RefundsRoute,
    identity: "Refund and Cancellation Policy",
  },
  {
    path: "/cookies",
    shell: "public",
    component: CookiesRoute,
    identity: "Cookie Policy",
  },
  {
    path: "/faq",
    shell: "public",
    component: FaqRoute,
    identity: `${brand.productName} FAQ`,
  },
  {
    path: "/status",
    shell: "public",
    component: StatusRoute,
    identity: "Status",
  },
  {
    path: "/dashboard",
    shell: "dashboard",
    component: DashboardOverviewRoute,
    identity: "Dashboard",
  },
  {
    path: "/dashboard/authentication",
    shell: "dashboard",
    component: AuthenticationDashboardRoute,
    identity: "Authentication",
  },
  {
    path: "/dashboard/authentication/users",
    shell: "dashboard",
    component: AuthenticationDashboardRoute,
    identity: "Users",
  },
  {
    path: "/dashboard/authentication/roles",
    shell: "dashboard",
    component: AuthenticationDashboardRoute,
    identity: "Roles",
  },
  {
    path: "/dashboard/authentication/groups",
    shell: "dashboard",
    component: AuthenticationDashboardRoute,
    identity: "Groups",
  },
  {
    path: "/dashboard/authentication/permissions",
    shell: "dashboard",
    component: AuthenticationDashboardRoute,
    identity: "Permissions",
  },
  {
    path: "/dashboard/database",
    shell: "dashboard",
    component: DatabaseOverviewRoute,
    identity: "Database",
  },
  {
    path: "/dashboard/database/:name/data",
    shell: "dashboard",
    component: DatabaseEntityDataRedirect,
    identity: "Database",
  },
  {
    path: "/dashboard/database/:name",
    shell: "dashboard",
    component: DatabaseEntityRoute,
    identity: "Database",
  },
  {
    path: "/dashboard/database/edit/:id",
    shell: "dashboard",
    component: EntityUpdatePage,
    identity: "Edit entity",
  },
  {
    path: "/dashboard/database/new",
    shell: "dashboard",
    component: EntityCreatePage,
    identity: "Create New Entity Definition",
  },
  {
    path: "/dashboard/functions",
    shell: "dashboard",
    component: FunctionWorkspace,
    identity: "Functions",
  },
  {
    path: "/dashboard/storage",
    shell: "dashboard",
    component: StorageDashboardRoute,
    identity: "Storage",
  },
  {
    path: "/dashboard/api-console",
    shell: "dashboard",
    component: ApiConsoleRoute,
    identity: "API console",
  },
  {
    path: "/dashboard/system",
    shell: "dashboard",
    component: SystemRoute,
    identity: "System",
  },
  {
    path: "/dashboard/assistant",
    shell: "dashboard",
    component: AssistantDashboardRoute,
    identity: "Assistant",
  },
  {
    path: "/dashboard/settings",
    shell: "dashboard",
    component: SettingsOverviewRoute,
    identity: "Workspace settings",
  },
  {
    path: "/dashboard/settings/general",
    shell: "dashboard",
    component: GeneralSettingsRoute,
    identity: "General settings",
  },
  {
    path: "/dashboard/settings/security",
    shell: "dashboard",
    component: SecuritySettingsRoute,
    identity: "Security settings",
  },
  {
    path: "/dashboard/settings/modules",
    shell: "dashboard",
    component: ModuleSettingsRoute,
    identity: "Module settings",
  },
  {
    path: "/dashboard/settings/smtp",
    shell: "dashboard",
    component: SmtpSettingsRoute,
    identity: "SMTP settings",
  },
  {
    path: "/dashboard/settings/team",
    shell: "dashboard",
    component: TeamMembersRoute,
    identity: "Workspace team",
  },
  {
    path: "/dashboard/settings/domains",
    shell: "dashboard",
    component: DomainsSettingsRoute,
    identity: "Domains",
  },
  {
    path: "/dashboard/query-builder",
    shell: "dashboard",
    component: QueryBuilderRoute,
    identity: "Query builder",
  },
  {
    path: "/dashboard/workflow",
    shell: "dashboard",
    component: WorkflowDashboardRoute,
    identity: "Workflow",
  },
  {
    path: "/dashboard/workflow/instances",
    shell: "dashboard",
    component: WorkflowInstancesRoute,
    identity: "Workflow instances",
  },
  {
    path: "/dashboard/workflow/instances/:instanceId",
    shell: "dashboard",
    component: WorkflowInstanceRoute,
    identity: "Instance tracker",
  },
  ...(
    [
      ["/dashboard/system/auditlogs", "Audit logs"],
      ["/dashboard/system/backups", "Backups"],
      ["/dashboard/system/codegenerator", "Code generators"],
      ["/dashboard/system/doctemplates", "Document templates"],
      ["/dashboard/system/recurringjobs", "Recurring jobs"],
      ["/dashboard/system/selectlists", "Select lists"],
      ["/dashboard/system/systemlogs", "System logs"],
    ] as const
  ).map(([path, identity]) => ({
    path,
    shell: "dashboard" as const,
    component: SystemRoute,
    identity,
  })),
  {
    path: "/dashboard/workflow/designer/:id",
    shell: "dashboard",
    component: WorkflowDesignerRoute,
    identity: "Workflow designer",
  },
  {
    path: "/dashboard/workflow/designer/new",
    shell: "dashboard",
    component: WorkflowDesignerRoute,
    identity: "New workflow",
  },
  {
    path: "/dashboard/workflow/new",
    shell: "dashboard",
    component: WorkflowDesignerRoute,
    identity: "New workflow",
  },
];

export function workspaceTenantCode(): string | undefined {
  const hostname =
    typeof globalThis.location === "object"
      ? globalThis.location.hostname
      : "localhost";
  const baseDomain = readEnv("VITE_GSB_BASE_DOMAIN");
  if (!baseDomain) throw new Error("VITE_GSB_BASE_DOMAIN is required");
  const resolution = resolveWorkspaceHost(hostname, baseDomain);
  return resolution.surface === "workspace" ? resolution.tenantCode : undefined;
}

export const createWorkspaceApp = (adapters: GsbHostAdapters) =>
  createGsbApp({
    features: [{ id: "auth", version: "0.2.0" }],
    routes: { public: publicRoutes, protected: protectedRoutes },
    theme: { name: "gsb-workspace" },
    i18n: { languages: AVAILABLE_LANGUAGES },
    adapters,
    ssr: { availableLanguages: AVAILABLE_LANGUAGES, notFound: () => null },
  });
